import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
  SimpleGrid,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import {
  FaCloud,
  FaSignOutAlt,
  FaArrowLeft,
  FaQuestionCircle,
  FaUserCircle,
  FaServer,
  FaCreditCard,
  FaShieldAlt,
  FaLifeRing,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const FAQ = () => {
  const { user, logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // PyFR / SRS only
  const allQuestions = [
    {
      question: "Who operates SRS?",
      answer: "SRS is developed and operated by Empirisch Tech GmbH, a company registered in Vienna, Austria (empirischtech.at). The platform is hosted under the srs.numericalai.net domain, which is Empirisch Tech's brand for Scale-Resolving Simulation of industrial flow problems."
    },
    {
      question: "What solver engine does SRS use?",
      answer: "SRS runs the PyFR open-source high-order CFD framework on NVIDIA GPUs. Cases are configured with a pyfr.ini file and executed through PyFR's GPU backends (e.g. CUDA)."
    },
    {
      question: "Why SRS — what makes it special?",
      answer: "Unlike traditional simulation tools, SRS is built on the high-order PyFR engine, advanced GPU acceleration, and AI-driven preflight validation. This combination reduces setup errors, speeds up scale-resolving turbulence simulations (LES, DNS, ILES), and improves productivity for industrial flow problems."
    },
    {
      question: "How fast is SRS?",
      answer: "When benchmarked against a 32-core CPU system, SRS achieves up to 10× faster performance on common PyFR workloads such as high-fidelity turbulence simulations. The speedup typically increases with case size and polynomial order. GPU parallelization, PyFR's CUDA backend, and optimized cloud infrastructure make this possible."
    },
    {
      question: "What should be inside the uploaded zip file?",
      answer: "Your ZIP must contain a pyfr.ini configuration file (exactly that name — not a folder) plus any mesh and auxiliary files referenced inside pyfr.ini (for example a Gmsh .msh mesh). Paths in pyfr.ini must match the files in the archive. See the Getting Started tutorial and the PyFR documentation at https://www.pyfr.org/documentation.html for case layout examples."
    },
    {
      question: "What is a pyfr.ini file?",
      answer: "pyfr.ini is the main input file for PyFR. It is an INI-style file with sections such as [solver], [backend], [mesh], [elements], [soln-plugin-*], and boundary conditions. It defines the equation system, mesh location, time integration, output formats, and plugins. Full reference: https://www.pyfr.org/documentation.html"
    },
    {
      question: "What mesh formats does PyFR support on SRS?",
      answer: "PyFR commonly uses Gmsh .msh meshes (mixed-element unstructured grids in 2D and 3D). Your mesh path in pyfr.ini must point to the mesh file included in your ZIP. For meshing guidance, refer to the PyFR user guide and the examples on the SRS platform."
    },
    {
      question: "What types of simulations can I run?",
      answer: "You can run Scale-Resolving Simulations (SRS) of turbulent flows with PyFR — including LES, DNS, and ILES — on unstructured mixed-element meshes in 2D and 3D. Typical applications include external aerodynamics, turbomachinery, wind-turbine wakes, and vortex-dominated flows. PyFR supports compressible and incompressible Navier–Stokes and related equation systems configured in pyfr.ini."
    },
    {
      question: "What output formats does PyFR write?",
      answer: "PyFR writes native .pyfrm solution files. You can also export VTK/PVTU (and other formats supported by your PyFR version) by setting the [output] section in pyfr.ini — for example format = pyfr, vtk — so results can be opened in VisIt, ParaView, or similar tools."
    },
    {
      question: "What is the difference between €1 Starter and Premium users?",
      answer: "New users activate their account with the €1 Starter plan, which includes two hours of compute time and basic GPU access. Purchasing Standard, Professional, or additional compute credits upgrades you to On Demand Usage (Premium), with priority queue access, advanced preflight validation, and AI-assisted debugging for PyFR cases."
    },
    {
      question: "What is the Enterprise subscription?",
      answer: "The Enterprise plan offers unlimited compute time with the GPUs of your choice (H100s, A100s, etc.) and a dedicated queue, allowing unlimited parallel PyFR simulations without waiting. It also provides tailored support and cost-effective solutions for large-scale SRS workloads."
    },
    {
      question: "How do I purchase the Enterprise subscription?",
      answer: "Click the 'Contact Sales' button under the Enterprise plan on the Buy Credits page, or use the contact form on the Settings page to request a quote. Our team will follow up shortly."
    },
    {
      question: "What is the maximum duration for a simulation?",
      answer: "If your simulation's estimated runtime exceeds 25 days, please contact us to explore a custom solution. Simulations running longer than 29 days are automatically cancelled."
    },
    {
      question: "Where can I find my invoices?",
      answer: "Go to the Settings page and open 'Manage Billing' to view and download your invoices."
    },
    {
      question: "How can I get my VAT back?",
      answer: "If you are registered as a business customer, you can reclaim VAT by providing your company name, address, and valid VAT ID through the contact form on the Settings page. Ensure your details are complete and accurate at checkout; we will then issue an invoice with VAT deducted."
    },
    {
      question: "What types of GPUs are used in your compute fleet?",
      answer: "Our compute fleet currently consists of NVIDIA A100 and H100 GPUs, offering high throughput for PyFR's GPU backends and large-scale engineering simulations."
    },
    {
      question: "Why is the processing time longer than the actual run time shown in the PyFR solver logs?",
      answer: "Processing time includes job initialization, environment setup, mesh and config staging, data transfer, and post-processing. The PyFR solver logs report only the core time-stepping runtime, not these platform overheads."
    },
    {
      question: "How does the queuing mechanism in SRS work?",
      answer: "€1 Starter users can run or queue only one job at a time, while Premium users can run and queue up to three jobs simultaneously, with faster access and shorter wait times."
    },
    {
      question: "How is the ETA of a simulation calculated and how accurate is it?",
      answer: "The estimated time is based on the average time per step reported by PyFR during the run and is refreshed as the job progresses. The ETA reflects solver runtime accurately; after the simulation finishes, additional steps such as post-processing, data transfer, and compression may add extra wall time (for example, up to about an hour for multi-GB result sets)."
    },
    {
      question: "How can I contact support?",
      answer: "Use the Contact Us form on the Settings page. Our support team typically responds within 24 hours on business days."
    },
    {
      question: "How accurate are the simulation results?",
      answer: "SRS uses PyFR's high-order numerical methods and validated turbulence models. Accuracy depends on your mesh resolution, polynomial order, and physical models defined in pyfr.ini. Platform examples and the PyFR documentation include benchmark cases for verification against analytical and experimental data where available."
    }
  ];

  // Filter questions based on search query
  const filteredQuestions = allQuestions.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Helmet>
        <title>Frequently Asked Questions | SRS</title>
        <meta name="description" content="Answers to common questions about SRS: running GPU cloud CFD simulations, the PyFR engine, credits and billing, data privacy, and platform support." />
        <link rel="canonical" href="https://srs.numericalai.net/faq" />
        <meta property="og:title" content="Frequently Asked Questions | SRS" />
        <meta property="og:description" content="Answers to common questions about SRS: running GPU cloud CFD simulations, the PyFR engine, credits and billing, data privacy, and platform support." />
        <meta property="og:url" content="https://srs.numericalai.net/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Frequently Asked Questions | SRS" />
        <meta name="twitter:description" content="Answers to common questions about SRS: running GPU cloud CFD simulations, the PyFR engine, credits and billing, data privacy, and platform support." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">            
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              {user ? (
                <Button 
                leftIcon={<FaCloud />}
                colorScheme="brand"
                size="sm"
                variant="ghost" as={RouterLink} to="/dashboard">
                  Dashboard
                </Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">
                  Sign In
                </Button>
              )}
              {user ? (
                <Button 
                  leftIcon={<FaSignOutAlt />}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    logout();
                  }}
                >
                  Sign Out
                </Button>
              ) : (
                <Button colorScheme="brand" as={RouterLink} to="/create-user">
                  Get Started
                </Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* FAQ Content */}
      <Container maxW="container.xl" py={12}>
        <VStack spacing={8} align="stretch">
          {/* Header Section */}
          <VStack spacing={4} textAlign="center">
            <Icon as={FaQuestionCircle} w={16} h={16} color="brand.500" />
            <Heading as="h1" size="2xl" color="gray.800">
              Frequently Asked Questions
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Find answers to common questions about SRS.
            </Text>
          </VStack>

          <Divider />

          {/* Search Bar */}
          <Box maxW="3xl" mx="auto" w="full">
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search questions and answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                borderRadius="full"
                bg="white"
                border="2px"
                borderColor="gray.200"
                _hover={{ borderColor: "brand.300" }}
                _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
              />
              {searchQuery && (
                <InputRightElement>
                  <IconButton
                    icon={<FaTimes />}
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  />
                </InputRightElement>
              )}
            </InputGroup>
            
            {/* Search Results Count */}
            <Text fontSize="sm" color="gray.600" mt={3} textAlign="center">
              {searchQuery ? (
                <>
                  Showing {filteredQuestions.length} of {allQuestions.length} questions
                  {filteredQuestions.length === 0 && " - Try different keywords"}
                </>
              ) : (
                `${allQuestions.length} questions available`
              )}
            </Text>
          </Box>

          {/* All FAQ Questions in One List */}
          {filteredQuestions.length > 0 ? (
            <Accordion allowMultiple>
              {filteredQuestions.map((faq, index) => (
                <AccordionItem key={index} border="1px" borderColor="gray.200" borderRadius="md" mb={2}>
                  <AccordionButton py={4} _hover={{ bg: 'gray.50' }}>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="semibold" color="gray.800" fontSize="md">
                        {faq.question}
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} pt={2} bg="gray.50">
                    <Text color="gray.600" lineHeight="1.8">
                      {faq.answer}
                    </Text>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Box textAlign="center" py={12}>
              <Icon as={FaQuestionCircle} w={12} h={12} color="gray.300" mb={4} />
              <Heading size="md" color="gray.500" mb={2}>
                No questions found
              </Heading>
              <Text color="gray.600" mb={4}>
                We couldn't find any questions matching "{searchQuery}"
              </Text>
              <Button 
                colorScheme="brand" 
                variant="outline"
                onClick={() => setSearchQuery('')}
              >
                Clear Search
              </Button>
            </Box>
          )}

          <Divider />

          {/* Quick Links */}
          <Box>
            <Heading size="md" color="gray.800" mb={4} textAlign="center">
              Related Resources
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Button 
                as={RouterLink} 
                to="/terms" 
                variant="outline" 
                colorScheme="brand"
                size="lg"
              >
                Terms of Service
              </Button>
              <Button 
                as={RouterLink} 
                to="/privacy" 
                variant="outline" 
                colorScheme="brand"
                size="lg"
              >
                Privacy Policy
              </Button>
              <Button 
                as={RouterLink} 
                to={user ? "/dashboard" : "/create-user"}
                colorScheme="brand"
                size="lg"
              >
                {user ? "Go to Dashboard" : "Get Started"}
              </Button>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default FAQ;